import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  ChevronLeft, PackageSearch, Building2, User, Phone, Mail, MapPin,
  CalendarClock, IndianRupee, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  new: "New", in_review: "In review", sourced: "Sourced", closed: "Closed",
};
const STATUS_COLOR = {
  new: "bg-brand-orange text-white",
  in_review: "bg-brand-yellow/40 text-brand-ink",
  sourced: "bg-green-100 text-green-800",
  closed: "bg-muted text-muted-foreground",
};

export default function AdminRequests() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, by_status: {}, by_type: {} });
  const [status, setStatus] = useState("all");

  const load = () => {
    const params = status === "all" ? {} : { status };
    api.get("/admin/requests", { params }).then(({ data }) => setItems(data));
    api.get("/admin/requests/stats").then(({ data }) => setStats(data));
  };
  useEffect(load, [status]);

  return (
    <div className="space-y-6" data-testid="admin-requests-page">
      <div>
        <Link to="/admin" className="inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground hover:text-foreground">
          <ChevronLeft size={12} /> Back to admin
        </Link>
        <h1 className="mt-2 font-heading text-3xl font-extrabold tracking-tight sm:text-4xl">
          Sourcing requests
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Outside products & services requested by customers and enterprises from the landing page.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-4">
        <Tile label="Total requests" value={stats.total || 0} icon={PackageSearch} color="#0A0A0A" />
        <Tile label="New" value={stats.by_status?.new || 0} icon={Sparkles} color="#FF3B30" />
        <Tile label="Customers" value={stats.by_type?.customer || 0} icon={User} color="#00E5FF" />
        <Tile label="Enterprises" value={stats.by_type?.enterprise || 0} icon={Building2} color="#FFD600" />
      </div>

      <Tabs value={status} onValueChange={setStatus}>
        <TabsList data-testid="request-status-tabs">
          <TabsTrigger value="all" data-testid="rtab-all">All</TabsTrigger>
          <TabsTrigger value="new" data-testid="rtab-new">New</TabsTrigger>
          <TabsTrigger value="in_review" data-testid="rtab-in_review">In review</TabsTrigger>
          <TabsTrigger value="sourced" data-testid="rtab-sourced">Sourced</TabsTrigger>
          <TabsTrigger value="closed" data-testid="rtab-closed">Closed</TabsTrigger>
        </TabsList>
      </Tabs>

      {items.length === 0 ? (
        <div className="rounded-2xl border border-dashed p-10 text-center text-sm text-muted-foreground">
          Nothing here yet.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => <RequestRow key={r.id} r={r} onChange={load} />)}
        </div>
      )}
    </div>
  );
}

function Tile({ label, value, icon: Icon, color }) {
  return (
    <div className="rounded-2xl border border-foreground/10 bg-white p-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">{label}</div>
          <div className="mt-1 font-heading text-2xl font-extrabold tracking-tight">{value}</div>
        </div>
        <div className="grid h-9 w-9 place-items-center rounded-xl text-white" style={{ background: color }}>
          <Icon size={14} />
        </div>
      </div>
    </div>
  );
}

function RequestRow({ r, onChange }) {
  const [notes, setNotes] = useState(r.admin_notes || "");
  const [saving, setSaving] = useState(false);

  const update = async (status, admin_notes) => {
    setSaving(true);
    try {
      const payload = {};
      if (status) payload.status = status;
      if (admin_notes !== undefined) payload.admin_notes = admin_notes;
      await api.patch(`/admin/requests/${r.id}`, payload);
      toast.success("Updated");
      onChange();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rounded-2xl border border-foreground/10 bg-white p-5" data-testid={`request-row-${r.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex-1 min-w-[260px]">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${STATUS_COLOR[r.status] || "bg-muted"}`}>
              {STATUS_LABEL[r.status] || r.status}
            </span>
            <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest">
              {r.requester_type === "enterprise" ? "Enterprise" : "Customer"} · {r.category}
            </span>
            {r.sentiment && (
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${
                r.sentiment === "positive" ? "bg-green-100 text-green-800"
                : r.sentiment === "negative" ? "bg-brand-orange/15 text-brand-orange"
                : "bg-brand-yellow/25 text-brand-ink"
              }`}>
                {r.sentiment}
              </span>
            )}
            <span className="text-[11px] text-muted-foreground">
              {(r.created_at || "").slice(0, 16).replace("T", " ")}
            </span>
          </div>
          <div className="mt-2 font-heading text-lg font-bold">{r.subject}</div>
          <div className="mt-1 text-sm leading-relaxed text-muted-foreground">{r.description}</div>
          <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-xs">
            <Meta icon={User} value={r.name} />
            {r.organization && <Meta icon={Building2} value={r.organization} />}
            <Meta icon={Phone} value={r.phone} />
            {r.email && <Meta icon={Mail} value={r.email} />}
            {(r.city || r.state) && <Meta icon={MapPin} value={[r.city, r.state].filter(Boolean).join(", ")} />}
            {r.needed_by && <Meta icon={CalendarClock} value={`by ${r.needed_by}`} />}
            {r.budget_inr && <Meta icon={IndianRupee} value={r.budget_inr} />}
            {r.quantity && <Meta icon={PackageSearch} value={r.quantity} />}
          </div>
        </div>
      </div>

      <div className="mt-4 space-y-2">
        <Textarea
          rows={2}
          placeholder="Internal notes — sourcing options, quotes, next step…"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          data-testid={`req-notes-${r.id}`}
        />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <Button
            variant="outline"
            className="rounded-full"
            disabled={saving}
            onClick={() => update(undefined, notes)}
            data-testid={`req-save-notes-${r.id}`}
          >
            Save notes
          </Button>
          <div className="flex flex-wrap gap-2">
            {["new", "in_review", "sourced", "closed"].filter((s) => s !== r.status).map((s) => (
              <Button
                key={s}
                size="sm"
                className={`rounded-full ${s === "sourced" ? "bg-green-600 text-white hover:bg-green-600/90"
                  : s === "closed" ? "bg-muted-foreground/30 text-foreground hover:bg-muted-foreground/40"
                  : "bg-brand-ink text-white hover:bg-brand-ink/90"}`}
                onClick={() => update(s)}
                disabled={saving}
                data-testid={`req-mark-${s}-${r.id}`}
              >
                Mark {STATUS_LABEL[s]}
              </Button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Meta({ icon: Icon, value }) {
  return (
    <span className="inline-flex items-center gap-1 text-muted-foreground">
      <Icon size={12} /> {value}
    </span>
  );
}
